import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { MCDANIEL_NAME } from "constants/settings.constants";

import { useShapefileTracking } from "components/project/layers/hooks/useShapefileTracking";
import { usePurgeDeletedShapefilesFromDefaults } from "components/user-settings/hooks";
import {
  DEFAULTS_PURGE_FAILURE_MESSAGES,
  reportDefaultsPurgeFailure
} from "components/user-settings/hooks/reportDefaultsPurgeFailure";

import { useManageLayerTree } from "../../../layers/hooks/manage-layer-tree/useManageLayerTree";
import { getManageSelectionState } from "../../../layers/hooks/manage-layer-tree/utils/getManageSelectionState";
import { useProjectLayerTree } from "../../../layers/hooks/project-layer-tree/useProjectLayerTree";
import { collectCheckedShapefileIds } from "../../../layers/utils/collectCheckedShapefileIds";
import { useProjectContext, useProjectDispatch } from "../../../projects/context";
import {
  useSelectedProject,
  useSelectedProjectPermissions
} from "../../../projects/hooks";
import {
  BulkDeleteAxiosError,
  type ProtectedItems,
  useDeleteShapefileListMutation
} from "../../mutations/delete-shapefile-list/useDeleteShapefileListMutation";

type TreeItem = { key: string; title: string; source?: string };

export interface UseDeleteShapefileListReturn {
  manageLayerTree: ReturnType<typeof useManageLayerTree>;
  isDeleteModalVisible: boolean;
  selectedItems: {
    folders: TreeItem[];
    shapefiles: TreeItem[];
  };
  handleModalOpen: () => void;
  handleDeleteConfirm: () => void;
  handleModalClose: () => void;
  deleteShapefiles: ReturnType<typeof useDeleteShapefileListMutation>;
  deleteError: string | null;
  protectedItems: ProtectedItems | null;
  deletedShapefiles: number;
  deletedFolders: number;
}

/**
 * Hook for managing shapefile deletion functionality in LayersTab.
 * Encapsulates all manage mode logic including tree state, modal state, and deletion operations.
 */
export const useDeleteShapefileList = (): UseDeleteShapefileListReturn => {
  const projectContext = useProjectContext();
  const projectDispatch = useProjectDispatch();
  const { tree } = useProjectLayerTree();
  // Use shared modal state from Project Context instead of local state
  const isDeleteModalVisible = projectContext.deleteModal.isVisible;
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [protectedItems, setProtectedItems] = useState<ProtectedItems | null>(null);
  const [deletedShapefiles, setDeletedShapefiles] = useState<number>(0);
  const [deletedFolders, setDeletedFolders] = useState<number>(0);

  // Add tracking hook
  const { trackBulkDelete } = useShapefileTracking();

  // Permission and project checks
  const { selectedProject: project } = useSelectedProject();
  const selectedProjectPermissions = useSelectedProjectPermissions();

  // Check if deletion is allowed based on permissions
  const canDeleteShapefiles = useMemo(() => {
    if (!project) return false;
    if (project.projectType === MCDANIEL_NAME) return false;
    if (!selectedProjectPermissions?.canEditProjectItems) return false;
    return true;
  }, [project, selectedProjectPermissions]);

  const manageLayerTree = useManageLayerTree({
    existingTree: tree
  });

  const purgeDeletedShapefilesFromDefaults = usePurgeDeletedShapefilesFromDefaults();

  const deleteShapefiles = useDeleteShapefileListMutation({
    projectId: projectContext.selectedProjectId || "",
    onSuccess: (data) => {
      // Check if there are any protected items
      const hasProtectedItems =
        data.protectedItems &&
        (data.protectedItems.shapefiles.length > 0 ||
          data.protectedItems.folders.length > 0);

      // Store deleted counts for display in modal
      setDeletedShapefiles(data.deletedShapefiles);
      setDeletedFolders(data.deletedFolders);

      if (hasProtectedItems) {
        // Partial success - show warning toast and keep modal open to show protected items
        toast.warning(`Some items could not be found, or removed due to an error.`);
        setDeleteError("Some items could not be deleted");
        setProtectedItems(data.protectedItems);
        // Don't close modal or clear selections - user needs to see protected items
      } else {
        // Complete success - show success toast and close modal
        toast.success(`Items deleted successfully.`);

        // Strip the deleted shapefiles' fields from the user's saved focus and
        // tooltip lists, which store raw property keys and would otherwise keep
        // requesting a field that no longer exists (EVA-126). Only on complete
        // success: a partial run leaves protected shapefiles alive, and purging
        // their fields would delete a working default. Best-effort, so it can
        // never block the modal close below - same pattern as
        // useMutationFieldDelete.
        try {
          purgeDeletedShapefilesFromDefaults(
            collectCheckedShapefileIds(tree, manageLayerTree.checkedKeys)
          );
        } catch (error) {
          reportDefaultsPurgeFailure(
            DEFAULTS_PURGE_FAILURE_MESSAGES.shapefilePurgeFailed,
            error
          );
        }

        clearCheckedKeys();
        projectDispatch({ payload: { deleteModal: { isVisible: false } } });
        setDeleteError(null);
        setProtectedItems(null);
      }
    },
    onError: (error: BulkDeleteAxiosError) => {
      // Show error toast
      toast.error("Failed to delete items. Please try again.");

      // Handle errors - don't close modal, show error details
      // If we have protected items info in the response, show it
      setDeleteError("Failed to delete some items");

      if (error.response?.data?.protectedItems) {
        setProtectedItems(error.response.data.protectedItems);
      }
    }
  });

  const clearCheckedKeys = useCallback(() => {
    projectDispatch({
      payload: {
        manageMode: {
          isEnabled: manageLayerTree.isManageMode,
          checkedKeys: []
        }
      }
    });
  }, [projectDispatch, manageLayerTree.isManageMode]);

  // Memoize selectedItems to avoid recalculating on every render
  const selectedItems = useMemo(() => {
    const items = {
      folders: [] as TreeItem[],
      shapefiles: [] as TreeItem[]
    };

    // Only calculate when modal is visible and we have selections
    if (isDeleteModalVisible && tree && manageLayerTree.checkedKeys.length > 0) {
      const findNodesByKeys = (nodes: typeof tree, targetKeys: string[]) => {
        for (const node of nodes) {
          if (targetKeys.includes(node.key)) {
            if (node.type === "folder") {
              items.folders.push({
                key: node.key,
                title: node.title,
                source: node.source || node.folder?.source
              });
            } else if (node.type === "layer") {
              items.shapefiles.push({
                key: node.key,
                title: node.title,
                source: node.source
              });
            }
          }
          if (node.children) {
            findNodesByKeys(node.children, targetKeys);
          }
        }
      };

      findNodesByKeys(tree, manageLayerTree.checkedKeys);
    }

    return items;
  }, [isDeleteModalVisible, tree, manageLayerTree.checkedKeys]);

  const handleModalOpen = useCallback(() => {
    // Only allow modal open if user has delete permissions
    if (!canDeleteShapefiles) {
      return;
    }
    projectDispatch({ payload: { deleteModal: { isVisible: true } } });
  }, [canDeleteShapefiles, projectDispatch]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!tree || !manageLayerTree.checkedKeys.length) return;

    if (!canDeleteShapefiles) {
      return;
    }

    // Clear previous errors and counts
    setDeleteError(null);
    setProtectedItems(null);
    setDeletedShapefiles(0);
    setDeletedFolders(0);

    const selectionState = getManageSelectionState(manageLayerTree.checkedKeys, tree);

    trackBulkDelete({
      shapefileIds: selectionState.shapefileIds.length,
      projectShapefileIds: selectionState.projectShapefileIds.length,
      folderIds: selectionState.folderIds.length,
      orgFolderIds: selectionState.orgFolderIds.length,
      systemFolderIds: selectionState.systemFolderIds.length
    });

    await deleteShapefiles.mutateAsync({
      shapefileIds: selectionState.shapefileIds,
      projectShapefileIds: selectionState.projectShapefileIds,
      folderIds: selectionState.folderIds,
      orgFolderIds: selectionState.orgFolderIds,
      systemFolderIds: selectionState.systemFolderIds
    });
  }, [deleteShapefiles, manageLayerTree, tree, canDeleteShapefiles, trackBulkDelete]);

  const handleModalClose = useCallback(() => {
    projectDispatch({ payload: { deleteModal: { isVisible: false } } });
  }, [projectDispatch]);

  return {
    manageLayerTree,
    isDeleteModalVisible,
    selectedItems,
    handleModalOpen,
    handleDeleteConfirm,
    handleModalClose,
    deleteShapefiles,
    deleteError,
    protectedItems,
    deletedShapefiles,
    deletedFolders
  };
};
